/**
 * Image Viewer Modal Controller
 *
 * Manages the fullscreen image viewer for viewing images at full size
 * @module portal/modules/ui/modals/imageViewerModal
 */

import { els } from '../elements';
import { captureFocus, restoreFocus } from './focusManager';

const bodyEl = document.body;

interface ImageData {
  src: string;
  alt: string;
}

let currentImages: ImageData[] = [];
let currentImageIndex = 0;

/**
 * Check if the image viewer modal is currently open
 */
export function isImageViewerOpen(): boolean {
  return !!(els.imageViewerModal && !els.imageViewerModal.hidden);
}

/**
 * Open the image viewer with a set of images
 * @param images - Array of image objects with src and alt properties
 * @param startIndex - Initial image index to display
 */
export function openImageViewer(images: ImageData[], startIndex = 0): void {
  if (!images || images.length === 0) return;

  captureFocus('lastActiveBeforeImageViewer');

  currentImages = images;
  currentImageIndex = Math.max(0, Math.min(startIndex, images.length - 1));

  displayImage();

  if (els.imageViewerBackdrop) {
    els.imageViewerBackdrop.hidden = false;
  }
  if (els.imageViewerModal) {
    els.imageViewerModal.hidden = false;
  }
  if (bodyEl) {
    bodyEl.classList.add('modal-open');
  }

  window.requestAnimationFrame(() => {
    if (els.closeImageViewer) {
      (els.closeImageViewer as HTMLElement).focus();
    }
  });
}

/**
 * Close the image viewer modal
 */
export function closeImageViewer(): void {
  if (els.imageViewerModal) {
    els.imageViewerModal.hidden = true;
  }
  if (els.imageViewerBackdrop) {
    els.imageViewerBackdrop.hidden = true;
  }
  if (bodyEl) {
    bodyEl.classList.remove('modal-open');
  }

  restoreFocus('lastActiveBeforeImageViewer');
}

/**
 * Show the next image
 */
export function nextImage(): void {
  if (currentImages.length === 0) return;
  currentImageIndex = (currentImageIndex + 1) % currentImages.length;
  displayImage();
}

/**
 * Show the previous image
 */
export function previousImage(): void {
  if (currentImages.length === 0) return;
  currentImageIndex = (currentImageIndex - 1 + currentImages.length) % currentImages.length;
  displayImage();
}

/**
 * Display the current image
 */
function displayImage(): void {
  if (currentImages.length === 0) return;

  const image = currentImages[currentImageIndex];
  if (!image) return;

  if (els.imageViewerImg) {
    els.imageViewerImg.src = image.src;
    els.imageViewerImg.alt = image.alt;
  }

  if (els.imageViewerCounter) {
    els.imageViewerCounter.textContent = `${currentImageIndex + 1} / ${currentImages.length}`;
  }

  // Update navigation button states
  if (els.imageViewerPrev) {
    (els.imageViewerPrev as HTMLButtonElement).disabled = currentImages.length <= 1;
  }
  if (els.imageViewerNext) {
    (els.imageViewerNext as HTMLButtonElement).disabled = currentImages.length <= 1;
  }
}
